/**
 * Product Reviews Modal
 *
 * Fetches Yotpo Storefront v3 reviews + bottomline for the current product,
 * renders a custom summary + infinite-scroll review list inside a slide-up
 * modal.
 *
 * Endpoints (https://apidocs.yotpo.com/v3.0/reference/storefront-product-reviews):
 *   GET https://api-cdn.yotpo.com/v3/storefront/store/{appKey}/product/{productId}/reviews?per_page=N&page=N
 *   GET https://api-cdn.yotpo.com/v3/storefront/store/{appKey}/product/{productId}/bottomline
 *
 * Auth: X-Yotpo-Token header set to the public app key (same value as the
 * store id segment of the URL).
 */

(() => {
  if (customElements.get('product-reviews-modal')) return;

  const API_HOST = 'https://api-cdn.yotpo.com';
  const PER_PAGE = 10;
  const PREWARM_TIMEOUT_MS = 3000;
  const PREWARM_FALLBACK_MS = 1500;

  const HTML_ESCAPE = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#39;',
  };

  const escapeHTML = (str) =>
    String(str == null ? '' : str).replace(/[&<>"']/g, (m) => HTML_ESCAPE[m]);

  const dotsHTML = (score, size) => {
    const out = [];
    for (let i = 1; i <= 5; i += 1) {
      let pct = 0;
      if (score >= i) pct = 100;
      else if (score > i - 1) pct = Math.round((score - (i - 1)) * 100);
      out.push(`<span class="product-reviews-modal__dot" style="--fill:${pct}%"></span>`);
    }
    const sizeClass = size === 'small' ? ' product-reviews-modal__dots--small' : '';
    return `<div class="product-reviews-modal__dots${sizeClass}" aria-hidden="true">${out.join('')}</div>`;
  };

  const formatDate = (iso, locale) => {
    if (!iso) return '';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    try {
      return new Intl.DateTimeFormat(locale || undefined, {
        month: 'long',
        year: 'numeric',
      }).format(d);
    } catch (_) {
      return d.toDateString();
    }
  };

  const reviewerName = (review, fallback) =>
    review?.user?.displayName ||
    review?.user?.display_name ||
    review?.name ||
    review?.reviewer?.name ||
    fallback;

  const reviewCreatedAt = (review) => review?.createdAt || review?.created_at || '';

  const pickBottomline = (data) => data?.bottomline || data?.response?.bottomline || data || {};

  const bottomlineScore = (bl) =>
    parseFloat(bl?.averageScore ?? bl?.average_score ?? bl?.score ?? 0) || 0;

  const bottomlineTotal = (bl) =>
    parseInt(bl?.totalReview ?? bl?.total_reviews ?? bl?.total_review ?? 0, 10) || 0;

  class ProductReviewsModal extends ModalWindow {
    constructor() {
      super();
      this.loaded = false;
      this.loading = false;
      this.done = false;
      this.page = 0;
      this.total = Infinity;
      this.shown = 0;
      this.controller = null;
      this.requestId = 0;
      this.boundHandleScroll = null;
      this.scrollCheckScheduled = false;
      this.boundProductChange = null;

      this.appKey = this.dataset.yotpoAppKey;
      this.productId = this.dataset.productId;
      this.locale = this.dataset.pageLocale || document.documentElement.lang || 'en';

      this.labels = {
        ratings: this.dataset.lRatings || 'Ratings',
        loading: this.dataset.lLoading || 'Loading…',
        empty: this.dataset.lEmpty || 'No reviews yet.',
        error: this.dataset.lError || 'Unable to load reviews. Please try again.',
        anonymous: this.dataset.lAnonymous || 'Anonymous',
        counter: this.dataset.lCounter || 'Showing {shown} of {total} reviews',
        endState: this.dataset.lEndState || "You've reached the end of the reviews.",
      };

      this.refs = {
        summary: this.querySelector('[data-reviews-summary]'),
        list: this.querySelector('[data-reviews-list]'),
        counter: this.querySelector('[data-reviews-counter]'),
        status: this.querySelector('[data-reviews-status]'),
        endState: this.querySelector('[data-reviews-end-state]'),
        sentinel: this.querySelector('[data-reviews-sentinel]'),
      };
    }

    show(opener) {
      this.opener = opener;
      this.style.display = 'block';
      this.classList.add('modal-show');

      if (typeof lazyLoadInstance !== 'undefined' && lazyLoadInstance) {
        lazyLoadInstance.update();
      }

      setTimeout(() => {
        document.body.style.overflow = 'hidden';
        this.classList.add('modal-visuallyshow');
      }, 1);

      requestAnimationFrame(() => {
        if (typeof trapFocus === 'function') trapFocus(this);
      });

      if (!this.loaded) {
        this.loaded = true;
        this.bootstrap();
      }
    }

    hide() {
      super.hide();
      this.controller?.abort();
      // Invalidate any awaited response (including a prewarm awaited mid-close).
      this.requestId += 1;
    }

    connectedCallback() {
      super.connectedCallback();
      this.schedulePrewarm();

      // Sibling-product swap inside a Combined Listing dispatches a bubbling
      // `product:change-requested` event from _DefaultProductOptionSelects.
      // Rebind our reviews fetch to the new productId.
      this.boundProductChange = this.handleProductChange.bind(this);
      document.addEventListener('product:change-requested', this.boundProductChange);
    }

    disconnectedCallback() {
      super.disconnectedCallback?.();
      if (this.boundProductChange) {
        document.removeEventListener('product:change-requested', this.boundProductChange);
        this.boundProductChange = null;
      }
    }

    schedulePrewarm() {
      if (this.prewarmScheduled) return;
      if (!this.appKey || !this.productId) return;
      this.prewarmScheduled = true;

      const run = () => this.prewarm();
      if ('requestIdleCallback' in window) {
        window.requestIdleCallback(run, { timeout: PREWARM_TIMEOUT_MS });
      } else {
        setTimeout(run, PREWARM_FALLBACK_MS);
      }
    }

    prewarm() {
      if (this.prewarmPromise || this.loaded) return;
      this.prewarmPromise = this.fetchYotpo(`reviews?perPage=${PER_PAGE}&page=1`).catch((err) => {
        if (err?.name !== 'AbortError') {
          console.warn('[product-reviews-modal] prewarm failed', err);
        }
        return null;
      });
    }

    async bootstrap() {
      if (!this.appKey || !this.productId) {
        this.setStatus(this.labels.error);
        return;
      }
      this.setupInfiniteScroll();
      await this.fetchNextPage();
    }

    setupInfiniteScroll() {
      // Idempotent — safe to call after a sibling-product reset.
      if (this.boundHandleScroll) return;
      this.boundHandleScroll = this.handleScroll.bind(this);
      this.addEventListener('scroll', this.boundHandleScroll, { passive: true });
      // Initial check: kicks off page 1 and handles the case where the first
      // page doesn't fill the modal viewport.
      this.scheduleScrollCheck();
    }

    teardownInfiniteScroll() {
      if (this.boundHandleScroll) {
        this.removeEventListener('scroll', this.boundHandleScroll);
        this.boundHandleScroll = null;
      }
      // Sentinel stays in DOM (1px, invisible) so a sibling-product reset can
      // reuse it without re-rendering markup.
    }

    handleScroll() {
      this.scheduleScrollCheck();
    }

    scheduleScrollCheck() {
      if (this.scrollCheckScheduled) return;
      this.scrollCheckScheduled = true;
      requestAnimationFrame(() => {
        this.scrollCheckScheduled = false;
        this.maybeLoadMore();
      });
    }

    maybeLoadMore() {
      if (this.loading || this.done) return;
      const remaining = this.scrollHeight - this.scrollTop - this.clientHeight;
      if (remaining < 600) this.fetchNextPage();
    }

    handleProductChange(event) {
      const newId = String(event?.detail?.productId || '');
      if (!newId || newId === String(this.productId)) return;
      this.resetForProduct(newId);
    }

    resetForProduct(newId) {
      this.controller?.abort();
      this.requestId += 1;
      this.prewarmPromise = null;
      this.prewarmScheduled = false;
      this.loaded = false;
      this.loading = false;
      this.done = false;
      this.page = 0;
      this.total = Infinity;
      this.shown = 0;

      this.teardownInfiniteScroll();

      if (this.refs.summary) this.refs.summary.innerHTML = '';
      if (this.refs.list) this.refs.list.innerHTML = '';
      if (this.refs.counter) {
        this.refs.counter.textContent = '';
        this.refs.counter.hidden = true;
      }
      if (this.refs.endState) {
        this.refs.endState.textContent = '';
        this.refs.endState.hidden = true;
      }
      this.setStatus('');

      this.productId = newId;
      this.dataset.productId = newId;

      if (this.classList.contains('modal-show')) {
        // Modal is open — fetch fresh reviews for the new product now.
        this.loaded = true;
        this.bootstrap();
      } else {
        // Modal closed — re-arm idle prewarm so the next open is instant.
        this.schedulePrewarm();
      }
    }

    async fetchYotpo(path, signal) {
      const url = `${API_HOST}/v3/storefront/store/${encodeURIComponent(this.appKey)}/product/${encodeURIComponent(this.productId)}/${path}`;
      const res = await fetch(url, {
        method: 'GET',
        headers: {
          accept: 'application/json',
          'Content-Type': 'application/json',
          'X-Yotpo-Token': this.appKey,
        },
        signal,
      });
      if (!res.ok) throw new Error(`Yotpo ${path} → HTTP ${res.status}`);
      return res.json();
    }

    async fetchNextPage() {
      if (this.loading || this.done) return;
      if (this.page * PER_PAGE >= this.total) return;
      this.loading = true;
      this.setStatus(this.labels.loading);

      const next = this.page + 1;
      const requestId = ++this.requestId;
      this.controller = new AbortController();

      try {
        let raw = null;
        if (next === 1 && this.prewarmPromise) {
          const prewarmed = this.prewarmPromise;
          this.prewarmPromise = null;
          raw = await prewarmed;
        }
        if (!raw) {
          raw = await this.fetchYotpo(
            `reviews?perPage=${PER_PAGE}&page=${next}`,
            this.controller.signal
          );
        }

        // Drop stale response — modal closed/reopened or a later page beat us.
        if (requestId !== this.requestId) return;

        // v3 storefront is flat; v1 widget wraps in `response` — handle both.
        const data = raw?.reviews ? raw : raw?.response || raw || {};
        const reviews = data.reviews || [];
        const pagination = data.pagination || {};
        const perPage = pagination.perPage || pagination.per_page || PER_PAGE;

        if (typeof pagination.total === 'number') this.total = pagination.total;
        this.page = pagination.page || next;

        if (next === 1 && reviews.length === 0) {
          this.setStatus(this.labels.empty);
          this.teardownInfiniteScroll();
          this.done = true;
          return;
        }

        if (next === 1 && !this.refs.summary.childElementCount) {
          this.renderSummary(data);
        }

        this.appendReviews(reviews);
        this.updateCounter();
        this.setStatus('');

        if (this.page * perPage >= this.total) {
          this.teardownInfiniteScroll();
          this.showEndState();
          this.done = true;
        } else {
          // Re-check after layout settles — recovers the "few short reviews
          // don't fill the viewport" edge case without runaway loading
          // (scrollHeight grows on append, so `remaining` increases).
          this.scheduleScrollCheck();
        }
      } catch (err) {
        if (err?.name === 'AbortError') return;
        console.error('[product-reviews-modal] reviews fetch failed', err);
        this.setStatus(this.labels.error);
      } finally {
        this.loading = false;
      }
    }

    renderSummary(data) {
      const bl = pickBottomline(data);
      const score = bottomlineScore(bl);
      const total = bottomlineTotal(bl);
      if (!total && !score) return;

      const formattedTotal = total.toLocaleString(this.locale);
      const formattedScore = score.toFixed(1);

      this.refs.summary.innerHTML = `
        <div class="product-reviews-modal__score">${escapeHTML(formattedScore)}</div>
        <div class="product-reviews-modal__summary-meta">
          ${dotsHTML(score)}
          <div class="product-reviews-modal__total">${escapeHTML(`${formattedTotal} ${this.labels.ratings}`)}</div>
        </div>
      `;
    }

    appendReviews(reviews) {
      if (!reviews.length) return;
      const html = reviews
        .map((r) => {
          const score = parseFloat(r.score ?? 0) || 0;
          const date = formatDate(reviewCreatedAt(r), this.locale);
          return `
            <article class="product-reviews-modal__review">
              <div class="product-reviews-modal__review-date">
                <span>${escapeHTML(date)}</span>
                <hr aria-hidden="true">
              </div>
              <div class="product-reviews-modal__review-header">
                <h3 class="product-reviews-modal__review-title">${escapeHTML(r.title || '')}</h3>
                <div class="product-reviews-modal__review-score">
                  ${dotsHTML(score, 'small')}
                  <span class="product-reviews-modal__review-score-num">${escapeHTML(score.toFixed(1))}</span>
                </div>
              </div>
              <p class="product-reviews-modal__review-content">${escapeHTML(r.content || '')}</p>
              <p class="product-reviews-modal__review-name">${escapeHTML(reviewerName(r, this.labels.anonymous))}</p>
            </article>
          `;
        })
        .join('');
      const tpl = document.createElement('template');
      tpl.innerHTML = html;
      this.refs.list.appendChild(tpl.content);
      this.shown += reviews.length;
    }

    updateCounter() {
      if (!this.refs.counter || !this.total || this.total === Infinity) return;
      const text = this.labels.counter
        .replace('{shown}', this.shown.toLocaleString(this.locale))
        .replace('{total}', this.total.toLocaleString(this.locale));
      this.refs.counter.textContent = text;
      this.refs.counter.hidden = false;
    }

    showEndState() {
      if (!this.refs.endState) return;
      this.refs.endState.textContent = this.labels.endState;
      this.refs.endState.hidden = false;
    }

    setStatus(text) {
      if (!this.refs.status) return;
      if (text) {
        this.refs.status.textContent = text;
        this.refs.status.hidden = false;
      } else {
        this.refs.status.textContent = '';
        this.refs.status.hidden = true;
      }
    }
  }

  customElements.define('product-reviews-modal', ProductReviewsModal);
})();
